/**
 * useSubdomainValidation — validates a subdomain input in four gates:
 *
 *   1. Format — caller-injected `validateFormat(subdomain) -> {valid, error?}`.
 *   2. Resolution — uses `@puffin-core/subdomain-routing` `resolveCompanyFromSubdomain`.
 *   3. Company config load — uses `@puffin-core/subdomain-routing` `loadCompanyConfiguration`.
 *   4. Caching + debouncing — results memoized by normalized subdomain.
 *
 * Returns a stable object shape (status + booleans + company info + error
 * + validateSubdomain + clearValidation).
 *
 * @module @puffin-core/login-shell/hooks/useSubdomainValidation
 */

import { useState, useCallback, useRef, useMemo, useEffect } from 'react';

import { useLoginTranslate } from './useLoginTranslate.js';
import {
  resolveCompanyFromSubdomain,
  loadCompanyConfiguration,
  ResolveStatus,
} from '@puffin-core/subdomain-routing';
import { RESERVED_SUBDOMAINS as CANONICAL_RESERVED_SUBDOMAINS } from '@puffin-core/reserved-subdomains';

export const ValidationStatus = Object.freeze({
  IDLE: 'idle',
  VALIDATING: 'validating',
  VALID: 'valid',
  INVALID: 'invalid',
  NOT_FOUND: 'not_found',
  ERROR: 'error',
});

const DEFAULT_DEBOUNCE_MS = 300;

// AUTH-LOGIN-FIDELITY-001: negative resolves (NOT_FOUND / ERROR) are cached only
// briefly so a repeated typo doesn't re-hit `/companies/resolve`, WITHOUT masking
// a tenant that was provisioned moments after a mistyped attempt. VALID resolves
// stay cached for the session (a real company doesn't disappear mid-session).
const NEGATIVE_RESULT_TTL_MS = 30_000;

/**
 * Whether a cached entry may still be served. VALID entries never expire within
 * the session; negative entries (NOT_FOUND / ERROR) expire after a short TTL so a
 * newly-provisioned tenant is re-checked instead of being permanently "not found".
 *
 * @param {{status: string, timestamp: number}|undefined} entry
 * @returns {boolean}
 */
function isCacheEntryUsable(entry) {
  if (!entry) return false;
  if (entry.status === ValidationStatus.VALID) return true;
  return Date.now() - entry.timestamp < NEGATIVE_RESULT_TTL_MS;
}

/**
 * Platform-conflict subdomains that must never be used as tenant subdomains.
 * These names are reserved for platform infrastructure (www = marketing,
 * api = backend, admin/console = platform admin, etc.). Matching is
 * case-insensitive; the validator normalizes input to lowercase before lookup.
 *
 * Re-exported verbatim from the zero-dependency `@puffin-core/reserved-subdomains`
 * SSOT so this client-side validator stays byte-identical to the server's
 * authoritative reserved list (previously this was a narrower hand-maintained
 * subset that could drift out of sync with the backend). Consumers that import
 * `RESERVED_SUBDOMAINS` from this module keep working unchanged.
 */
export const RESERVED_SUBDOMAINS = CANONICAL_RESERVED_SUBDOMAINS;

const SUBDOMAIN_ALLOWED_CHARS = /^[a-z0-9-]+$/;
const SUBDOMAIN_MIN_LENGTH = 3;
const SUBDOMAIN_MAX_LENGTH = 63; // RFC 1035 label limit

/**
 * Default subdomain format validator for `useSubdomainValidation`. Enforces:
 *   - Required (non-empty after trim + lowercase)
 *   - ≥ 3 characters, ≤ 63 characters (RFC 1035 label limit)
 *   - Characters restricted to `[a-z0-9-]` (case-insensitive on input)
 *   - No leading or trailing hyphen
 *   - Not in `RESERVED_SUBDOMAINS`
 *
 * Cross-domain login consumers (`MarketingLoginPage`, any future tenant-entry
 * flow) import and pass this as `useSubdomainValidation({ validateFormat })`
 * so they don't each re-implement DNS label rules.
 *
 * @param {unknown} value  Candidate subdomain string.
 * @returns {{valid: boolean, error?: string}}
 */
export function defaultSubdomainFormatValidator(value) {
  if (typeof value !== 'string' || value.trim() === '') {
    return { valid: false, error: 'Subdomain is required' };
  }
  const normalized = value.trim().toLowerCase();
  if (normalized.length < SUBDOMAIN_MIN_LENGTH) {
    return {
      valid: false,
      error: `Subdomain must be at least ${SUBDOMAIN_MIN_LENGTH} characters`,
    };
  }
  if (normalized.length > SUBDOMAIN_MAX_LENGTH) {
    return {
      valid: false,
      error: `Subdomain must be ${SUBDOMAIN_MAX_LENGTH} characters or fewer`,
    };
  }
  if (!SUBDOMAIN_ALLOWED_CHARS.test(normalized)) {
    return {
      valid: false,
      error: 'Subdomain may only contain letters, numbers, and hyphens',
    };
  }
  if (normalized.startsWith('-') || normalized.endsWith('-')) {
    return {
      valid: false,
      error: 'Subdomain cannot start or end with a hyphen',
    };
  }
  if (RESERVED_SUBDOMAINS.includes(normalized)) {
    return { valid: false, error: 'This subdomain is reserved' };
  }
  return { valid: true };
}

export function useSubdomainValidation({ validateFormat, debounceMs = DEFAULT_DEBOUNCE_MS } = {}) {
  const [status, setStatus] = useState(ValidationStatus.IDLE);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [error, setError] = useState(null);

  const refs = useRef({
    debounceTimer: null,
    cache: new Map(),
    isMounted: true,
  });

  useEffect(() => {
    const current = refs.current;
    current.isMounted = true;
    return () => {
      current.isMounted = false;
      if (current.debounceTimer) clearTimeout(current.debounceTimer);
    };
  }, []);

  // AAS-I18N-002 W2 / ruling 4 (Q-R44-5): four user-facing English literals lived
  // here — two as `setError('…')` call arguments and TWO as an object PROPERTY on
  // the cached error entry (`errored.error = '…'`, the identical sentence written
  // at two branches). All four render straight into the login form's inline
  // validation. Only the two call arguments were in the 30-site HIGH census; the
  // two property assignments are a measured recall gap (RECIPE miss-class 2).
  //
  // The translator is parked in a REF: `validateSubdomain` is a memoised callback
  // consumers drive from their own effects, so putting a translated string in its
  // dep array would re-fire subdomain validation on every language change.
  const translate = useLoginTranslate(undefined);
  const messagesRef = useRef({ translate });
  useEffect(() => {
    messagesRef.current = { translate };
  }, [translate]);

  const validateSubdomain = useCallback(
    (subdomain) => {
      if (refs.current.debounceTimer) clearTimeout(refs.current.debounceTimer);

      if (!subdomain || subdomain.trim() === '') {
        setStatus(ValidationStatus.INVALID);
        setError(messagesRef.current.translate('subdomain.error.required'));
        setCompanyInfo(null);
        return Promise.resolve();
      }

      const normalized = subdomain.trim().toLowerCase();

      const cached = refs.current.cache.get(normalized);
      if (isCacheEntryUsable(cached)) {
        setStatus(cached.status);
        setCompanyInfo(cached.companyInfo);
        setError(cached.error);
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        refs.current.debounceTimer = setTimeout(async () => {
          // No `if (!isMounted) return resolve()` guard here — the useEffect
          // cleanup (line 124-126) clears `debounceTimer` synchronously when
          // the component unmounts, so this setTimeout callback never runs
          // post-unmount. Subsequent `await`s in this async function CAN
          // race with unmount, so isMounted checks remain after each await.
          if (validateFormat) {
            const format = validateFormat(normalized);
            if (!format.valid) {
              setStatus(ValidationStatus.INVALID);
              setError(
                format.error || messagesRef.current.translate('subdomain.error.invalidFormat'),
              );
              setCompanyInfo(null);
              return resolve();
            }
          }

          setStatus(ValidationStatus.VALIDATING);
          setError(null);

          try {
            // SUBDOMAIN-RESOLVE-001: the resolver now returns a discriminated
            // result ({status,…}) instead of `string|null`. This lets the
            // validator distinguish a genuine miss (NOT_FOUND — a typo) from a
            // transient/errored resolve (ERROR — a cold-start hiccup), where the
            // old null-collapse rendered both as "not found".
            const resolution = await resolveCompanyFromSubdomain(normalized);
            if (!refs.current.isMounted) return resolve();

            if (resolution.status === ResolveStatus.NOT_FOUND) {
              const notFound = {
                status: ValidationStatus.NOT_FOUND,
                companyInfo: null,
                error: 'Company subdomain not found. Please check the spelling.',
                timestamp: Date.now(),
              };
              refs.current.cache.set(normalized, notFound);
              setStatus(notFound.status);
              setError(notFound.error);
              setCompanyInfo(null);
              return resolve();
            }

            if (resolution.status === ResolveStatus.ERROR) {
              const errored = {
                status: ValidationStatus.ERROR,
                companyInfo: null,
                error: messagesRef.current.translate('subdomain.error.validationFailed'),
                timestamp: Date.now(),
              };
              refs.current.cache.set(normalized, errored);
              setStatus(errored.status);
              setError(errored.error);
              setCompanyInfo(null);
              return resolve();
            }

            const config = await loadCompanyConfiguration(resolution.companyId);
            if (!refs.current.isMounted) return resolve();

            const result = {
              status: ValidationStatus.VALID,
              companyInfo: config,
              error: null,
              timestamp: Date.now(),
            };
            refs.current.cache.set(normalized, result);
            setStatus(result.status);
            setCompanyInfo(result.companyInfo);
            setError(null);
            resolve();
          } catch {
            if (!refs.current.isMounted) return resolve();
            const errored = {
              status: ValidationStatus.ERROR,
              companyInfo: null,
              error: messagesRef.current.translate('subdomain.error.validationFailed'),
              timestamp: Date.now(),
            };
            refs.current.cache.set(normalized, errored);
            setStatus(errored.status);
            setError(errored.error);
            setCompanyInfo(null);
            resolve();
          }
        }, debounceMs);
      });
    },
    [validateFormat, debounceMs],
  );

  const clearValidation = useCallback(() => {
    if (refs.current.debounceTimer) clearTimeout(refs.current.debounceTimer);
    setStatus(ValidationStatus.IDLE);
    setCompanyInfo(null);
    setError(null);
  }, []);

  const isValid = status === ValidationStatus.VALID;
  const isValidating = status === ValidationStatus.VALIDATING;
  const companyName = companyInfo?.name || null;

  return useMemo(
    () => ({
      status,
      isValid,
      isValidating,
      companyInfo,
      companyName,
      error,
      validateSubdomain,
      clearValidation,
    }),
    [
      status,
      isValid,
      isValidating,
      companyInfo,
      companyName,
      error,
      validateSubdomain,
      clearValidation,
    ],
  );
}

export default useSubdomainValidation;
